/* eslint-disable react/no-unescaped-entities */
/**
 * Regulatory Reporting · README (in-app, post sign-in).
 *
 * Living document. Grows section-by-section as we ship each feature.
 * Sidebar entry: Umbrella "Regulatory Reporting" → "Regulatory README".
 *
 * Structure: left rail of anchored sections + right scroll body.
 * Renders live values (backend version, tenant regulator flags, section
 * count) from /api/health and /api/regulatory/* where useful.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  BookOpen, Compass, LayoutDashboard, Landmark, Database, FileCheck2,
  Building2, ShieldCheck, ScrollText, ChevronRight, CheckCircle2, Circle,
  Loader2, ArrowUpRight, Radio, User, AlertTriangle, Layers, GitBranch,
} from "lucide-react";

const NAVY = "#0A1F44";
const NAVY_DARK = "#061633";
const GOLD = "#C9A24A";

const SECTIONS = [
  { id: "overview",           label: "Overview",             icon: Compass,        status: "live" },
  { id: "reporting-dashboard",label: "Reporting Dashboard",  icon: LayoutDashboard,status: "live" },
  { id: "omni-risk",          label: "OMNI-Risk Return",     icon: Landmark,       status: "live" },
  { id: "data-lake",          label: "Regulatory Data Lake", icon: Database,       status: "phase-2" },
  { id: "submissions",        label: "Submission History",   icon: FileCheck2,     status: "phase-4" },
  { id: "multi-regulator",    label: "Multi-Regulator",      icon: Building2,      status: "framework" },
  { id: "delivery-plan",      label: "Delivery Plan",        icon: GitBranch,      status: "info" },
];

export default function RegulatoryReadmePage() {
  const [health, setHealth]   = useState(null);
  const [sections, setSecs]   = useState([]);
  const [config, setConfig]   = useState(null);
  const [active, setActive]   = useState("overview");

  useEffect(() => {
    Promise.all([
      api.get("/health").catch(() => ({ data: null })),
      api.get("/regulatory/omni/sections").catch(() => ({ data: { sections: [] } })),
      api.get("/regulatory/config").catch(() => ({ data: null })),
    ]).then(([h, s, c]) => {
      setHealth(h.data);
      setSecs(s.data.sections);
      setConfig(c.data);
    });
  }, []);

  useEffect(() => {
    // Scroll-spy: pick the section whose top is nearest above the viewport centre.
    const opts = { root: null, rootMargin: "-30% 0px -60% 0px", threshold: 0 };
    const io = new IntersectionObserver((entries) => {
      const inView = entries.filter((e) => e.isIntersecting);
      if (inView.length) setActive(inView[0].target.id);
    }, opts);
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, [sections.length]);

  const jump = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const enabledRegulators = useMemo(
    () => (config?.tenant_config?.regulators || []).join(" · ") || "None",
    [config],
  );

  return (
    <div className="min-h-screen" style={{ background: "#F8F9FA" }} data-testid="regulatory-readme">
      {/* Hero ─────────────────────────────────────────── */}
      <Hero health={health} regulators={enabledRegulators} sectionsCount={sections.length} />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 pb-16 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
        {/* Left rail ─────────────────────────────────── */}
        <aside className="lg:sticky lg:top-6 self-start">
          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-2" data-testid="readme-rail">
            <div className="px-3 py-2 text-[10px] uppercase tracking-[0.24em] font-semibold text-slate-400">
              Contents
            </div>
            <nav className="flex flex-col gap-0.5">
              {SECTIONS.map((s) => {
                const Icon = s.icon;
                const isActive = active === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => jump(s.id)}
                    data-testid={`rail-${s.id}`}
                    className="group flex items-center gap-2 px-3 py-2 rounded-lg text-[12.5px] text-left transition"
                    style={{
                      background: isActive ? `${NAVY}` : "transparent",
                      color: isActive ? "white" : "#334155",
                    }}
                  >
                    <Icon className="w-3.5 h-3.5 shrink-0" />
                    <span className="flex-1 truncate">{s.label}</span>
                    <StatusDot status={s.status} />
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Body ─────────────────────────────────────── */}
        <div className="space-y-10 max-w-3xl">
          <Overview />
          <ReportingDashboardSection />
          <OmniRiskSection sections={sections} />
          <DataLakeSection />
          <SubmissionsSection />
          <MultiRegulatorSection config={config} />
          <DeliveryPlanSection />
        </div>
      </div>
    </div>
  );
}

/* ─── Hero ─────────────────────────────────────────── */

function Hero({ health, regulators, sectionsCount }) {
  return (
    <section
      className="relative overflow-hidden text-white"
      style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_DARK} 65%, #020814 100%)` }}
    >
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full" style={{ background: `${GOLD}18` }} />
      <div className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full" style={{ background: `${GOLD}0F` }} />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: GOLD }}>
          <BookOpen className="w-3 h-3" />
          Regulatory Reporting · README
        </div>
        <h1 className="font-display text-3xl lg:text-5xl font-semibold mt-3 leading-tight max-w-3xl">
          A mini FSCA IRS for your institution
        </h1>
        <p className="mt-4 text-white/70 max-w-2xl leading-relaxed">
          Living reference for the Regulatory Reporting module. Explains what each surface does,
          how it sources its data, and where each feature sits on the delivery timeline.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-3 text-[11px]">
          <HeroPill label="Backend" value={health?.version || "—"} />
          <HeroPill label="Repo backend" value={health?.repository_backend || "—"} />
          <HeroPill label="Sections" value={`${sectionsCount || 12} OMNI`} />
          <HeroPill label="Regulators enabled" value={regulators} tone="gold" />
        </div>
      </div>
    </section>
  );
}

function HeroPill({ label, value, tone = "slate" }) {
  const styles = tone === "gold"
    ? { border: `1px solid ${GOLD}88`, background: `${GOLD}22`, color: "#FBEACC" }
    : { border: "1px solid rgba(255,255,255,0.18)", background: "rgba(255,255,255,0.06)", color: "white" };
  return (
    <span className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 font-mono" style={styles}>
      <span className="uppercase tracking-widest text-[9px] text-white/50">{label}</span>
      <span className="tabular-nums">{value}</span>
    </span>
  );
}

/* ─── Section shell ────────────────────────────────── */

function Section({ id, icon: Icon, kicker, title, status, children }) {
  return (
    <section id={id} className="scroll-mt-8" data-testid={`section-${id}`}>
      <header className="mb-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] font-semibold text-slate-400">
          <Icon className="w-3 h-3" />
          {kicker}
          {status && <StatusChip status={status} />}
        </div>
        <h2 className="font-display text-2xl lg:text-3xl font-semibold mt-1" style={{ color: NAVY }}>
          {title}
        </h2>
      </header>
      <div className="space-y-4 text-[14px] text-slate-700 leading-relaxed">{children}</div>
    </section>
  );
}

/* ─── #1 Overview ──────────────────────────────────── */

function Overview() {
  return (
    <Section
      id="overview"
      icon={Compass}
      kicker="Overview"
      title="What we're building"
      status="live"
    >
      <Card>
        <p>
          The <strong>Regulatory Reporting</strong> module is Altara's answer to the FSCA's
          <strong> Integrated Regulatory Solution (IRS)</strong>. It gives every institution one
          cockpit for producing a defensible OMNI-Risk Return — plus, over time, every other
          statutory return an institution owes to the FSCA, FIC, PA, or the Ombud.
        </p>
        <p className="mt-3">
          The design is <strong>data-lake first</strong>: the module doesn't ask compliance
          officers to type answers. It pulls values directly from the institution's operational
          systems (Altara-native or external connectors), tags each value with provenance,
          and only surfaces to a human what is truly missing, ambiguous, or overridden.
        </p>
      </Card>
      <Card>
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-400 mb-2">
          The four principles
        </div>
        <ul className="space-y-2.5">
          <Principle title="Auto-source everything possible">
            Every field starts with a data-lake source. Manual entry is the exception, not the norm.
          </Principle>
          <Principle title="Every value is defensible">
            Provenance is captured at capture-time: source system, extraction timestamp, computation
            method, confidence, human-override flag.
          </Principle>
          <Principle title="Validation is regulator-grade">
            The FSCA's own rules sheet (from Annexure A) drives blocking errors and warnings.
            Severity scoring runs off the FSCA's 157×29 severity matrix.
          </Principle>
          <Principle title="One institution, many regulators">
            Same platform, same data lake — feeds OMNI-Risk to FSCA, risk assessment to FIC,
            CBR to PA, statistics to Ombud. Tenant-gated so each institution only sees what applies.
          </Principle>
        </ul>
      </Card>
    </Section>
  );
}

function Principle({ title, children }) {
  return (
    <li className="flex gap-3">
      <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#059669" }} />
      <div>
        <div className="font-semibold text-[13px]" style={{ color: NAVY }}>{title}</div>
        <div className="text-[13px] text-slate-600 leading-relaxed">{children}</div>
      </div>
    </li>
  );
}

/* ─── #2 Reporting Dashboard ───────────────────────── */

function ReportingDashboardSection() {
  return (
    <Section
      id="reporting-dashboard"
      icon={LayoutDashboard}
      kicker="Feature 1"
      title="Reporting Dashboard"
      status="live"
    >
      <Card>
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-400 mb-2">
          Route · <code className="text-[11px] bg-slate-100 px-1.5 py-0.5 rounded">/regulatory/dashboard</code>
        </div>
        <p>
          The Reporting Dashboard is <strong>the landing screen</strong> for the whole module.
          Its job is to answer, in one glance: <em>"How ready are we to submit our next OMNI-Risk
          return, and what's blocking us?"</em>
        </p>
        <p className="mt-3">
          A compliance officer walking into this screen at 8am should know within 15 seconds
          which sections are late, which owners need a nudge, and whether validation errors
          are trending up or down. No spreadsheets, no dashboards spread across three vendors.
        </p>
      </Card>

      <Card>
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-400 mb-2">
          Anatomy of the screen
        </div>
        <div className="space-y-3">
          <Anatomy num="1" title="Navy · gold hero with quarter selector">
            The reporting period, submission due date, current status (Draft · In Review ·
            Validated · Submitted · Accepted · Rejected), plus a global completion progress bar.
            Refresh and Export buttons live here.
          </Anatomy>
          <Anatomy num="2" title="Four status tiles">
            <span className="italic">Next submission due</span> (with days-remaining traffic light),
            <span className="italic"> Overall completion</span>,
            <span className="italic"> Validation errors</span> (blocking + warnings),
            <span className="italic"> Data-lake freshness</span> (how many of the 12 sections are wired).
          </Anatomy>
          <Anatomy num="3" title="12-section OMNI-Risk heatmap">
            One card per canonical section. Shows: number + title, owner, completion % + count,
            source chip (Altara Native / Data Connector / Manual / Not wired), validation errors
            + warnings, last-refreshed timestamp, expected source system. Every card is clickable
            and opens the section workspace (Phase 3).
          </Anatomy>
          <Anatomy num="4" title="Activity feed">
            Every event that touched this reporting period — ingestion runs, human overrides,
            validation passes, status transitions. Full audit trail one click away.
          </Anatomy>
        </div>
      </Card>

      <Card>
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-400 mb-2">
          What powers it (technical)
        </div>
        <ul className="space-y-1.5">
          <TechRow k="Backend endpoint" v="GET /api/regulatory/omni/periods/{period_id}/dashboard" />
          <TechRow k="Tenant scope" v="Enforced via Depends(tenant_scope). 401 without auth." />
          <TechRow k="Auto-provision"  v="First visit for a tenant lazily creates the current-quarter reporting period." />
          <TechRow k="Data-lake sources" v="Every OMNI value must come from the institution's own systems (core-banking, complaints register, HRIS, ITSM, GL). Delivered via connector adapters — no Altara consumer-portal data is ever used to file a return." />
          <TechRow k="Refresh" v="POST-triggered ingestion, async. Updates last-refreshed on completion." />
        </ul>
      </Card>

      <Card tone="amber">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#D97706" }} />
          <div>
            <div className="text-[13px] font-semibold" style={{ color: "#92400E" }}>Phase 1 caveat</div>
            <div className="text-[12.5px] text-slate-700 mt-1">
              Every section starts at 0% and unsourced. Data-lake ingestion,
              field-level completion percentages and validation counts arrive in later
              phases — we do not auto-assign ownership, sources, or values from the
              template alone.
            </div>
          </div>
        </div>
      </Card>
    </Section>
  );
}

function Anatomy({ num, title, children }) {
  return (
    <div className="flex gap-3">
      <span
        className="w-6 h-6 shrink-0 rounded-md text-[11px] font-mono font-semibold flex items-center justify-center text-white"
        style={{ background: NAVY }}
      >
        {num}
      </span>
      <div className="min-w-0">
        <div className="text-[13px] font-semibold" style={{ color: NAVY }}>{title}</div>
        <div className="text-[12.5px] text-slate-600 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function TechRow({ k, v }) {
  return (
    <li className="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-4 border-t border-slate-100 py-1.5 first:border-t-0 first:pt-0">
      <span className="text-[10.5px] uppercase tracking-widest text-slate-400 md:w-40 shrink-0 font-semibold">{k}</span>
      <span className="text-[12.5px] text-slate-700 font-mono">{v}</span>
    </li>
  );
}

/* ─── #3 OMNI-Risk Return ──────────────────────────── */

function OmniRiskSection({ sections }) {
  return (
    <Section
      id="omni-risk"
      icon={Landmark}
      kicker="Feature 2"
      title="OMNI-Risk Return · 12-section workspace"
      status="live"
    >
      <Card>
        <p>
          The OMNI-Risk Return is the FSCA's <strong>foundational cross-sector return</strong>,
          replacing the prior OMNI-CBR and feeding the IRS's automated risk model. Every
          FSCA-supervised institution reports it. GO-LIVE September 2026.
        </p>
        <p className="mt-3">
          Every section is a first-class workspace. Fields, mappings, and values sit behind a
          four-layer architecture — a regulatory field catalogue (immutable, from Annexure A),
          a data-lake catalogue (per-tenant, grown organically), a mapping bridge (LLM-suggested
          or human-approved), and reported values (with provenance).
        </p>
      </Card>

      <Card>
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-400 mb-2">
          Four-layer architecture (built)
        </div>
        <div className="space-y-2.5">
          <Anatomy num="1" title="Regulatory field catalogue — 337 capture points">
            Parsed from Annexure A into 337 immutable field definitions (218 options + 119 conditional
            children). Every field has a stable ref like <code className="text-[11px] bg-slate-100 px-1.5 py-0.5 rounded">OMNI-9.1.a</code>,
            typed (number / amount / boolean / date / text / yesno / enum), and required flag.
          </Anatomy>
          <Anatomy num="2" title="Data-lake catalogue — per tenant">
            Institution-scoped canonical fields. Altara-native sources (Consent, Complaints, KYC, ...)
            seeded automatically; external connectors (HRIS, GL, ...) added iteratively.
          </Anatomy>
          <Anatomy num="3" title="Mapping bridge — LLM-suggested, human-approved">
            Every OMNI field can map to one lake field. Seed mappings (Section 9) ship with 95%+
            confidence; other sections use the LLM-powered mapping suggester (Claude Sonnet 4.5 via
            the LLM port). ≥90% confidence auto-accepts; below that awaits human review.
          </Anatomy>
          <Anatomy num="4" title="Reported values — with provenance">
            Refresh pulls current lake values, tags them with source system + timestamp + confidence,
            and writes to <code className="text-[11px] bg-slate-100 px-1.5 py-0.5 rounded">omni_field_values</code>.
            Human overrides carry actor + reason and take precedence.
          </Anatomy>
        </div>
      </Card>

      <Card>
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-400 mb-2">
          The 12 sections
        </div>
        <ol className="divide-y divide-slate-100">
          {(sections.length ? sections : Array.from({ length: 12 }, (_, i) => ({ n: i + 1 }))).map((s) => (
            <li key={s.n} className="py-2 flex items-baseline gap-3">
              <span className="w-6 shrink-0 text-[11px] font-mono font-semibold text-right" style={{ color: NAVY }}>
                {String(s.n).padStart(2, "0")}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold text-slate-800 truncate">{s.title || "—"}</div>
              </div>
              {s.n === 9 && (
                <span className="text-[9px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded shrink-0"
                  style={{ background: "#D1FAE5", color: "#065F46" }}>
                  Wired end-to-end
                </span>
              )}
            </li>
          ))}
        </ol>
      </Card>

      <Card tone="slate">
        <p className="text-[13px] text-slate-700">
          <strong>Section 9 · Complaints Management</strong> is the reference implementation —
          10 fields, 10 native mappings, live refresh from the Consumer Assistance domain.
          Every other section renders its fields but shows no value until either a data-lake
          source is seeded, mapped, and refreshed, or a compliance officer overrides manually.
        </p>
      </Card>
    </Section>
  );
}

/* ─── #4 Data Lake ─────────────────────────────────── */

function DataLakeSection() {
  return (
    <Section
      id="data-lake"
      icon={Database}
      kicker="Feature 3"
      title="Regulatory Data Lake"
      status="phase-2"
    >
      <Card>
        <p>
          The <strong>Regulatory Data Lake</strong> is a normalised, tenant-isolated store of every
          field any regulator asks for. Sources are of three kinds:
        </p>
        <ul className="mt-3 space-y-1.5 text-[13px]">
          <li className="flex items-baseline gap-2">
            <span className="inline-block text-[9px] uppercase tracking-widest font-mono font-semibold px-1.5 rounded"
              style={{ background: "#D1FAE5", color: "#065F46" }}>Altara native</span>
            <span>Consent Ledger, Consumer Register, KYC Orchestration, Complaints, Journeys, Trust Credentials.</span>
          </li>
          <li className="flex items-baseline gap-2">
            <span className="inline-block text-[9px] uppercase tracking-widest font-mono font-semibold px-1.5 rounded"
              style={{ background: "#DBEAFE", color: "#1E40AF" }}>Connector</span>
            <span>HRIS (governance, headcount, diversity), GL (financial data), policy admin (terminations, lapse), marketing platform (advertising).</span>
          </li>
          <li className="flex items-baseline gap-2">
            <span className="inline-block text-[9px] uppercase tracking-widest font-mono font-semibold px-1.5 rounded"
              style={{ background: "#FEF3C7", color: "#92400E" }}>Manual</span>
            <span>The compliance officer's typed answer, always with a reason and a timestamp.</span>
          </li>
        </ul>
      </Card>

      <Card>
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-400 mb-2">
          Why this matters
        </div>
        <p>
          The <strong>same data lake</strong> feeds the FSCA OMNI-Risk, the FIC risk assessment,
          the PA CBR and the Ombud return. An institution only wires each source system once,
          and Altara maps its fields into whichever return the regulator asks for.
        </p>
      </Card>
    </Section>
  );
}

/* ─── #5 Submission History ────────────────────────── */

function SubmissionsSection() {
  return (
    <Section
      id="submissions"
      icon={FileCheck2}
      kicker="Feature 4"
      title="Submission History &amp; XBRL export"
      status="phase-4"
    >
      <Card>
        <p>
          When a period is validated and signed off, Altara produces a submission package:
          an XLSX for human inspection and an <strong>XBRL package</strong> for FSCA IRS ingestion.
          Every package is SHA-256 signed and time-stamped in an immutable audit table.
        </p>
        <p className="mt-3">
          If the FSCA later publishes a direct API for pilot participants, that lands as a
          push-adapter in the same submissions engine — no separate workflow.
        </p>
      </Card>
    </Section>
  );
}

/* ─── #6 Multi-Regulator ───────────────────────────── */

function MultiRegulatorSection({ config }) {
  const enabled = new Set(config?.tenant_config?.regulators || []);
  const catalogue = config?.regulators || [];
  return (
    <Section
      id="multi-regulator"
      icon={Building2}
      kicker="Framework"
      title="Multi-regulator, tenant-gated"
      status="framework"
    >
      <Card>
        <p>
          Not every institution reports to every regulator. Some are FSCA-only; some also owe
          returns to the FIC (AML/CFT) or the Prudential Authority. Altara treats regulators as
          first-class flags on the tenant. Enable the ones that apply — sidebar items and
          modules light up accordingly.
        </p>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {catalogue.length === 0 && (
          <div className="col-span-2 rounded-xl border border-dashed border-slate-200 bg-white p-6 text-[13px] text-slate-500 text-center">
            Loading regulator catalogue…
          </div>
        )}
        {catalogue.map((r) => (
          <div key={r.code} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-[11px] font-mono font-semibold shrink-0"
                style={{ background: r.colour }}>
                {r.code}
              </span>
              <div className="min-w-0">
                <div className="text-[13px] font-semibold truncate" style={{ color: NAVY }}>{r.name}</div>
                <div className="text-[10.5px] uppercase tracking-widest text-slate-400">{r.kind.replace(/_/g, " ")}</div>
              </div>
              {enabled.has(r.code) ? (
                <span className="ml-auto inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full"
                  style={{ background: "#D1FAE5", color: "#065F46" }}>
                  <CheckCircle2 className="w-2.5 h-2.5" /> Enabled
                </span>
              ) : (
                <span className="ml-auto text-[9px] uppercase tracking-widest text-slate-400 font-semibold">Off</span>
              )}
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {r.returns.map((code) => (
                <span key={code} className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ background: "#F1F5F9", color: "#475569" }}>
                  {code.replace(/_/g, " ")}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ─── #7 Delivery Plan ─────────────────────────────── */

function DeliveryPlanSection() {
  const phases = [
    { p: "Phase 1", state: "done",     title: "Foundation + Reporting Dashboard",
      body: "Data model shipped, sidebar umbrella live, dashboard rendering with tiles, heatmap, activity, section drill-in scaffolds." },
    { p: "Phase 2", state: "next",     title: "Native ingestion (Sections 4, 6, 9)",
      body: "Wire Altara's own domain (Consent, Complaints, KYC) into OmniFieldValue rows with provenance. Real completion %." },
    { p: "Phase 3", state: "queued",   title: "Section wizards for all 12 sections",
      body: "Field-card UI, data-lake sourced values, provenance chips, human overrides, inline validation." },
    { p: "Phase 4", state: "queued",   title: "Validation + submission package (XBRL)",
      body: "Rules engine from Annexure A, severity-score computation from 157×29 matrix, SHA-256 signed package export." },
    { p: "Phase 5", state: "ongoing",  title: "External connectors (per tenant)",
      body: "HRIS, GL, policy admin, marketing platform adapters. Iterative and tenant-scoped." },
  ];
  return (
    <Section
      id="delivery-plan"
      icon={GitBranch}
      kicker="Roadmap"
      title="Delivery plan"
      status="info"
    >
      <Card>
        <p>
          Delivered in five phases, sequenced so that at every phase-end the module is
          <strong> demo-ready and defensible</strong> — no half-shipped surfaces.
        </p>
      </Card>
      <div className="space-y-2">
        {phases.map((ph) => <PhaseRow key={ph.p} {...ph} />)}
      </div>
    </Section>
  );
}

function PhaseRow({ p, state, title, body }) {
  const tone = {
    done:    { bg: "#D1FAE5", fg: "#065F46", icon: CheckCircle2, dot: "#059669", label: "shipped" },
    next:    { bg: "#DBEAFE", fg: "#1E40AF", icon: Loader2,      dot: "#0369A1", label: "up next" },
    queued:  { bg: "#F1F5F9", fg: "#475569", icon: Circle,       dot: "#94A3B8", label: "queued" },
    ongoing: { bg: "#FEF3C7", fg: "#92400E", icon: Circle,       dot: "#D97706", label: "ongoing" },
  }[state] || { bg: "#F1F5F9", fg: "#475569", icon: Circle, dot: "#94A3B8", label: state };
  const Icon = tone.icon;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${tone.dot}18` }}>
          <Icon className={`w-4 h-4 ${state === "next" ? "animate-spin" : ""}`} style={{ color: tone.dot }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[11px] font-semibold text-slate-400">{p}</span>
            <span className="text-[14px] font-display font-semibold" style={{ color: NAVY }}>{title}</span>
            <span className="text-[9px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded" style={{ background: tone.bg, color: tone.fg }}>
              {tone.label}
            </span>
          </div>
        </div>
      </div>
      <p className="text-[12.5px] text-slate-600 pl-11 leading-relaxed">{body}</p>
    </div>
  );
}

/* ─── Shared ────────────────────────────────────────── */

function Card({ children, tone = "white" }) {
  const styles = {
    white: "bg-white border-slate-200",
    slate: "bg-slate-50 border-slate-200",
    amber: "bg-amber-50 border-amber-200",
  }[tone] || "bg-white border-slate-200";
  return <div className={`rounded-2xl border p-5 lg:p-6 shadow-sm ${styles}`}>{children}</div>;
}

function StatusChip({ status }) {
  const map = {
    live:      { label: "LIVE",       bg: "#D1FAE5", fg: "#065F46" },
    "phase-1": { label: "PHASE 1",    bg: "#DBEAFE", fg: "#1E40AF" },
    "phase-2": { label: "PHASE 2",    bg: "#FEF3C7", fg: "#92400E" },
    "phase-4": { label: "PHASE 4",    bg: "#F1F5F9", fg: "#475569" },
    framework: { label: "FRAMEWORK",  bg: "#EDE9FE", fg: "#5B21B6" },
    info:      { label: "PLAN",       bg: "#F1F5F9", fg: "#475569" },
  }[status] || { label: status?.toUpperCase() || "", bg: "#F1F5F9", fg: "#475569" };
  return (
    <span className="text-[9px] uppercase tracking-widest font-mono font-semibold px-1.5 py-0.5 rounded ml-2"
      style={{ background: map.bg, color: map.fg }}>
      {map.label}
    </span>
  );
}

function StatusDot({ status }) {
  const colour = {
    live:       "#059669",
    "phase-1":  "#0369A1",
    "phase-2":  "#D97706",
    "phase-4":  "#94A3B8",
    framework:  "#5B21B6",
    info:       "#94A3B8",
  }[status] || "#94A3B8";
  return <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: colour }} />;
}
